import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { settingsService } from '../../services/settings.service';
import api from '../../services/api';

interface SettingsForm {
  company_name: string;
  company_logo_url: string;
  primary_color: string;
  csat_threshold_critical: string;
  csat_threshold_warning: string;
  csat_threshold_good: string;
  survey_expiry_days: string;
  reminder_enabled: string;
  alert_email: string;
}

const DEFAULT_SETTINGS: SettingsForm = {
  company_name: '',
  company_logo_url: '',
  primary_color: '#4f46e5',
  csat_threshold_critical: '50',
  csat_threshold_warning: '70',
  csat_threshold_good: '80',
  survey_expiry_days: '14',
  reminder_enabled: 'true',
  alert_email: '',
};

export default function Settings() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<SettingsForm>(DEFAULT_SETTINGS);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsService.getSettings(),
  });

  useEffect(() => {
    if (settings && Array.isArray(settings)) {
      const mapped: SettingsForm = { ...DEFAULT_SETTINGS };
      for (const setting of settings) {
        if (setting?.key && setting.key in mapped) {
          mapped[setting.key as keyof SettingsForm] = String(setting.value ?? '');
        }
      }
      setForm(mapped);
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: async (entries: { key: string; value: string }[]) => {
      await Promise.all(
        entries.map((entry) => settingsService.updateSetting(entry.key, entry.value)),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const [subscribeSuccess, setSubscribeSuccess] = useState('');
  const [subscribeError, setSubscribeError] = useState('');

  const subscribeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/settings/subscribe-alerts');
      return response.data;
    },
    onSuccess: (data) => {
      setSubscribeSuccess(data.message || 'Subscription request sent. Check your email to confirm.');
      setSubscribeError('');
      setTimeout(() => setSubscribeSuccess(''), 5000);
    },
    onError: (error: any) => {
      setSubscribeError(error.response?.data?.message || 'Failed to subscribe. Make sure alert email is saved first.');
      setSubscribeSuccess('');
      setTimeout(() => setSubscribeError(''), 5000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const entries = Object.entries(form).map(([key, value]) => ({ key, value }));
    updateMutation.mutate(entries);
  };

  const handleChange = (key: keyof SettingsForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Cog6ToothIcon className="h-7 w-7 text-gray-400" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage branding and CSAT thresholds
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Branding Section */}
        <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Branding</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                Company Name
              </label>
              <input
                type="text"
                id="company_name"
                value={form.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Your Company"
              />
            </div>
            <div>
              <label htmlFor="company_logo_url" className="block text-sm font-medium text-gray-700">
                Logo URL
              </label>
              <input
                type="url"
                id="company_logo_url"
                value={form.company_logo_url}
                onChange={(e) => handleChange('company_logo_url', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="https://example.com/logo.png"
              />
            </div>
            <div>
              <label htmlFor="primary_color" className="block text-sm font-medium text-gray-700">
                Primary Color
              </label>
              <div className="mt-1 flex items-center gap-3">
                <input
                  type="color"
                  id="primary_color"
                  value={form.primary_color}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  className="h-10 w-10 cursor-pointer rounded border border-gray-300"
                />
                <input
                  type="text"
                  value={form.primary_color}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="#4f46e5"
                />
              </div>
            </div>
          </div>
        </div>

        {/* CSAT Thresholds Section */}
        <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">CSAT Thresholds</h2>
          <p className="mb-4 text-sm text-gray-500">
            Define score thresholds for alerts and reporting. Scores are on a 1-5 scale.
          </p>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <label htmlFor="csat_critical" className="block text-sm font-medium text-gray-700">
                Critical (below)
              </label>
              <input
                type="number"
                id="csat_critical"
                value={form.csat_threshold_critical}
                onChange={(e) => handleChange('csat_threshold_critical', e.target.value)}
                min="1"
                max="5"
                step="0.1"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-red-600">Triggers critical alert</p>
            </div>
            <div>
              <label htmlFor="csat_warning" className="block text-sm font-medium text-gray-700">
                Warning (below)
              </label>
              <input
                type="number"
                id="csat_warning"
                value={form.csat_threshold_warning}
                onChange={(e) => handleChange('csat_threshold_warning', e.target.value)}
                min="1"
                max="5"
                step="0.1"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-yellow-600">Triggers warning alert</p>
            </div>
            <div>
              <label htmlFor="csat_good" className="block text-sm font-medium text-gray-700">
                Good (above)
              </label>
              <input
                type="number"
                id="csat_good"
                value={form.csat_threshold_good}
                onChange={(e) => handleChange('csat_threshold_good', e.target.value)}
                min="1"
                max="5"
                step="0.1"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-green-600">Considered good performance</p>
            </div>
          </div>
        </div>

        {/* Survey Defaults Section */}
        <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Survey Defaults</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="survey_expiry" className="block text-sm font-medium text-gray-700">
                Default Expiry (days)
              </label>
              <input
                type="number"
                id="survey_expiry"
                value={form.survey_expiry_days}
                onChange={(e) => handleChange('survey_expiry_days', e.target.value)}
                min="1"
                max="90"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="reminder_enabled" className="block text-sm font-medium text-gray-700">
                Auto Reminders
              </label>
              <select
                id="reminder_enabled"
                value={form.reminder_enabled}
                onChange={(e) => handleChange('reminder_enabled', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Alert Notifications Section */}
        <div className="rounded-lg bg-white p-6 shadow ring-1 ring-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Alert Notifications</h2>
          <p className="mb-4 text-sm text-gray-500">
            Configure email alerts when a client's CSAT score drops below the critical threshold.
          </p>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="alert_email" className="block text-sm font-medium text-gray-700">
                Alert Email
              </label>
              <input
                type="email"
                id="alert_email"
                value={form.alert_email}
                onChange={(e) => handleChange('alert_email', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="alerts@yourcompany.com"
              />
              <p className="mt-1 text-xs text-gray-500">
                Email address to receive CSAT alert notifications.
              </p>
            </div>
            <div className="flex items-end">
              <div>
                <button
                  type="button"
                  onClick={() => subscribeMutation.mutate()}
                  disabled={subscribeMutation.isPending || !form.alert_email}
                  className="inline-flex items-center rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-500 disabled:opacity-50"
                >
                  {subscribeMutation.isPending ? 'Subscribing...' : 'Subscribe to Alerts'}
                </button>
                <p className="mt-2 text-xs text-amber-700">
                  ⚠️ After subscribing, AWS will send a confirmation email. You must confirm it to receive alerts.
                </p>
                {subscribeSuccess && (
                  <p className="mt-1 text-xs text-green-600">{subscribeSuccess}</p>
                )}
                {subscribeError && (
                  <p className="mt-1 text-xs text-red-600">{subscribeError}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3">
          {saveSuccess && (
            <span className="text-sm text-green-600">Settings saved successfully!</span>
          )}
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
